import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
    Add16Regular,
    ChevronDown12Regular,
    Dismiss12Regular,
    Document16Regular,
    Notebook16Regular,
    Open16Regular,
    Shield16Regular,
    Warning16Regular,
} from '@fluentui/react-icons';
import { DrawerBody, OverlayDrawer } from '@fluentui/react-components';
import { toast } from 'react-toastify';
import AskAiQuickCreateSidebar from '../AskAiQuickCreateSidebar';
import TaskFormSidebar from '../../TaskManagement/TaskFormSidebar';
import { saveInsightsModuleToJournal, buildInsightsItemJournalBody } from '../../../utils/askLensJournal';
import {
    filterSupportedInsightsActions,
    isInsightsReadOnlyBlocked,
} from '../../../utils/intelligenceInsightsActions';
import axiosInstance from '../../../apiServices/axios';
import { INTELLIGENCE_INSIGHTS_V2_REMEDIATION_ESTIMATE_REVIEW } from '../../../apiServices/api_routes';
import { withInsightsContextQuery } from '../../../utils/intelligenceInsightsContext';
import InsightsModuleChrome from './InsightsModuleChrome';
import { useInsightsReadOnly } from './InsightsReadOnlyContext';
import HighlightsEvidencePanel from './HighlightsEvidencePanel';
import ModuleEmptyState from './ModuleEmptyState';
import drawerStyles from './intelligenceInsightsHighlights.module.css';
import styles from './intelligenceInsightsRemediation.module.css';

const NAV_HORIZONS = {
    all: null,
    pre_close: new Set(['pre_close', 'day_1']),
    first_100_days: new Set(['first_100_days']),
    year_1: new Set(['year_1']),
    year_2_plus: new Set(['year_2', 'medium_term']),
};

function PriorityBadge({ value, label }) {
    const key = String(value || 'medium').toLowerCase();
    const cls =
        key === 'critical'
            ? styles.priorityCritical
            : key === 'high'
              ? styles.priorityHigh
              : styles.priorityMedium;
    return (
        <span className={`${styles.priority} ${cls}`}>
            <span className={styles.priorityDot} aria-hidden />
            {label || (key === 'medium' ? 'Medium' : key.charAt(0).toUpperCase() + key.slice(1))}
        </span>
    );
}

function moneyOrDash(label) {
    return !label || label === '—' ? <span className={styles.muted}>—</span> : label;
}

function yearFields(prefix, values, onChange) {
    const keys = [
        ['capex_min', 'CAPEX min'],
        ['capex_max', 'CAPEX max'],
        ['opex_min', 'OPEX min'],
        ['opex_max', 'OPEX max'],
    ];
    return keys.map(([field, label]) => (
        <label key={`${prefix}.${field}`}>
            <span className={styles.fieldLabel}>{label}</span>
            <input
                className={styles.fieldInput}
                type="number"
                min="0"
                inputMode="numeric"
                value={values[field] ?? ''}
                placeholder="0"
                onChange={e => onChange(prefix, field, e.target.value)}
            />
        </label>
    ));
}

function EstimateGrid({ estimate }) {
    return (
        <div className={styles.estimateGrid}>
            {[
                ['Y1 CAPEX', estimate?.year1CapexLabel],
                ['Y1 OPEX', estimate?.year1OpexLabel],
                ['Y2 CAPEX', estimate?.year2CapexLabel],
                ['Y2 OPEX', estimate?.year2OpexLabel],
            ].map(([label, value]) => (
                <div key={label} className={styles.estimateCell}>
                    <p className={styles.estimateHead}>{label}</p>
                    <p className={`${styles.estimateVal} ${!value || value === '—' ? styles.muted : ''}`}>
                        {value || '—'}
                    </p>
                </div>
            ))}
        </div>
    );
}

function formatReviewedWhen(iso) {
    if (!iso) return '';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

function titleCase(value) {
    return String(value || '')
        .replace(/_/g, ' ')
        .replace(/\b\w/g, ch => ch.toUpperCase());
}

function DetailDrawer({ open, item, onClose, onAction, readOnly }) {
    const [showOriginal, setShowOriginal] = useState(false);
    if (!open || !item) return null;
    const estimate = item.estimate || {};
    const review = item.latestReview;
    const humanReviewed = Boolean(estimate.humanReviewed || review);
    const actions = filterSupportedInsightsActions(item.actions || [], {
        moduleKey: 'remediation_investment',
        context: { item },
    });
    const original = item.lensOriginal || {};

    return (
        <OverlayDrawer
            open={open}
            position="end"
            modalType="modal"
            mountNode={typeof document !== 'undefined' ? document.body : undefined}
            onOpenChange={(_, data) => {
                if (!data.open) onClose?.();
            }}
            style={{ width: 480, maxWidth: '100vw', background: '#fff' }}>
            <DrawerBody className={drawerStyles.detailDrawerBody} style={{ width: 480, background: '#fff' }}>
                <div className={styles.drawerRoot}>
                    <div className={styles.drawerHeader}>
                        <div>
                            <h2 className={styles.drawerTitle}>{item.title}</h2>
                            <div className={styles.drawerChips}>
                                <PriorityBadge value={item.priority} label={item.priorityLabel} />
                                <span className={styles.timingChip}>{item.timeHorizonLabel}</span>
                                {humanReviewed ? (
                                    <span className={styles.reviewedChip}>Human reviewed</span>
                                ) : null}
                            </div>
                        </div>
                        <button type="button" className={styles.drawerClose} onClick={onClose} aria-label="Close">
                            <Dismiss12Regular />
                        </button>
                    </div>

                    <div className={styles.drawerBody}>
                        {item.summary ? (
                            <section className={styles.section}>
                                <p className={styles.sectionLabel}>Remediation</p>
                                <p className={styles.sectionBody}>{item.summary}</p>
                            </section>
                        ) : null}
                        {item.whyRecommended ? (
                            <section className={styles.section}>
                                <p className={styles.sectionLabel}>Why this is recommended</p>
                                <p className={styles.sectionBody}>{item.whyRecommended}</p>
                            </section>
                        ) : null}
                        {item.objective ? (
                            <section className={styles.section}>
                                <p className={styles.sectionLabel}>Desired outcome</p>
                                <p className={styles.sectionBody}>{item.objective}</p>
                            </section>
                        ) : null}
                        <section className={styles.section}>
                            <p className={styles.sectionLabel}>Timing & priority</p>
                            <div className={styles.metaPair}>
                                <div>
                                    <p className={styles.metaKey}>Timing</p>
                                    <p className={styles.metaVal}>{item.timeHorizonLabel}</p>
                                </div>
                                <div>
                                    <p className={styles.metaKey}>Priority</p>
                                    <div style={{ marginTop: 4 }}>
                                        <PriorityBadge value={item.priority} label={item.priorityLabel} />
                                    </div>
                                </div>
                            </div>
                        </section>
                        <section className={styles.section}>
                            <p className={styles.sectionLabel}>Investment estimate</p>
                            <EstimateGrid estimate={estimate} />
                            {humanReviewed ? (
                                <p className={styles.reviewedNote}>
                                    {[
                                        'Human reviewed',
                                        formatReviewedWhen(review?.reviewedAt),
                                        review?.reviewedByName,
                                    ]
                                        .filter(Boolean)
                                        .join(' · ')}
                                </p>
                            ) : null}
                            {humanReviewed && original ? (
                                <button
                                    type="button"
                                    className={styles.originalToggle}
                                    onClick={() => setShowOriginal(openOriginal => !openOriginal)}>
                                    View Lens original estimate
                                    <ChevronDown12Regular />
                                </button>
                            ) : null}
                            {showOriginal ? (
                                <div style={{ marginTop: 8 }}>
                                    <EstimateGrid
                                        estimate={{
                                            year1CapexLabel: original.year_1_capex_label,
                                            year1OpexLabel: original.year_1_opex_label,
                                            year2CapexLabel: original.year_2_capex_label,
                                            year2OpexLabel: original.year_2_opex_label,
                                        }}
                                    />
                                </div>
                            ) : null}
                            <p className={styles.estimateNote}>
                                Estimate confidence: {estimate.confidence || 'Not available'} · Basis:{' '}
                                {estimate.sourceLabel || 'Not available'}
                            </p>
                        </section>
                        {estimate.basisRationale ? (
                            <section className={styles.section}>
                                <p className={styles.sectionLabel}>Estimate basis</p>
                                <p className={styles.sectionBody}>{estimate.basisRationale}</p>
                            </section>
                        ) : null}
                        {item.assumptions?.length ? (
                            <section className={styles.section}>
                                <p className={styles.sectionLabel}>Assumptions</p>
                                <ul className={styles.assumptions}>
                                    {item.assumptions.map(line => (
                                        <li key={line}>{line}</li>
                                    ))}
                                </ul>
                            </section>
                        ) : null}
                        {item.relatedFindings?.length ? (
                            <section className={styles.section}>
                                <p className={styles.sectionLabel}>Related findings</p>
                                {item.relatedFindings.map(finding => (
                                    <button
                                        key={finding.id}
                                        type="button"
                                        className={styles.relatedLink}
                                        onClick={() =>
                                            onAction?.({
                                                key: 'open_key_findings',
                                                target: finding,
                                            })
                                        }>
                                        <Document16Regular />
                                        {finding.label || finding.id}
                                        <Open16Regular />
                                    </button>
                                ))}
                            </section>
                        ) : null}
                        {item.relatedRisks?.length ? (
                            <section className={styles.section}>
                                <p className={styles.sectionLabel}>Related risks</p>
                                {item.relatedRisks.map(risk => (
                                    <button
                                        key={risk.id}
                                        type="button"
                                        className={styles.relatedLink}
                                        onClick={() =>
                                            onAction?.({
                                                key: 'open_risks_signals',
                                                target: risk,
                                            })
                                        }>
                                        <Warning16Regular />
                                        {risk.label || risk.id}
                                        <Open16Regular />
                                    </button>
                                ))}
                            </section>
                        ) : null}
                        <section className={styles.section}>
                            <p className={styles.sectionLabel}>Evidence</p>
                            <button
                                type="button"
                                className={styles.relatedLink}
                                onClick={() => onAction?.({ key: 'view_evidence' })}>
                                <Document16Regular />
                                View evidence
                            </button>
                        </section>
                    </div>

                    <div className={styles.drawerFooter}>
                        {actions.map(action => (
                            <button
                                key={action.key}
                                type="button"
                                className={styles.actionBtn}
                                disabled={isInsightsReadOnlyBlocked(readOnly, action)}
                                onClick={() => onAction?.(action)}>
                                {action.key === 'create_task' ? <Add16Regular /> : null}
                                {action.key === 'add_to_raid' ? <Shield16Regular /> : null}
                                {action.key === 'save_to_journal' ? <Notebook16Regular /> : null}
                                {action.label}
                            </button>
                        ))}
                    </div>
                </div>
            </DrawerBody>
        </OverlayDrawer>
    );
}

function buildReviewFormState(item) {
    const estimate = item?.estimate || {};
    const y1 = estimate.year1 || {};
    const y2 = estimate.year2 || {};
    return {
        year_1: {
            capex_min: y1.capex_min ?? '',
            capex_max: y1.capex_max ?? '',
            opex_min: y1.opex_min ?? '',
            opex_max: y1.opex_max ?? '',
        },
        year_2: {
            capex_min: y2.capex_min ?? '',
            capex_max: y2.capex_max ?? '',
            opex_min: y2.opex_min ?? '',
            opex_max: y2.opex_max ?? '',
        },
        currency: estimate.currency || 'GBP',
        estimate_basis: 'human_refined_benchmark',
        confidence: String(estimate.confidence || 'medium').toLowerCase() || 'medium',
        review_note: '',
    };
}

function ReviewEstimateModal({ open, item, saving, error, onClose, onSave }) {
    const estimate = item?.estimate || {};
    const original = item?.lensOriginal || {};
    const [form, setForm] = useState(() => buildReviewFormState(item));

    useEffect(() => {
        if (open && item) {
            setForm(buildReviewFormState(item));
        }
    }, [open, item?.id, item?.treatmentId]);

    if (!open || !item || typeof document === 'undefined') return null;

    const setYear = (yearKey, field, value) => {
        setForm(prev => ({
            ...prev,
            [yearKey]: { ...prev[yearKey], [field]: value },
        }));
    };

    return createPortal(
        <div className={styles.modalScrim} role="presentation" onClick={onClose}>
            <div
                className={styles.modal}
                role="dialog"
                aria-modal="true"
                aria-labelledby="review-estimate-title"
                onClick={e => e.stopPropagation()}>
                <div className={styles.modalHeader}>
                    <div>
                        <h2 id="review-estimate-title" className={styles.modalTitle}>
                            Review investment estimate
                        </h2>
                        <p className={styles.modalSub}>
                            Refine the indicative range for this remediation. The original Lens
                            estimate remains in the audit history.
                        </p>
                    </div>
                    <button type="button" className={styles.drawerClose} onClick={onClose} aria-label="Close">
                        <Dismiss12Regular />
                    </button>
                </div>
                <div className={styles.modalBody}>
                    <div className={styles.originalBox}>
                        <p className={styles.sectionLabel}>Lens original estimate</p>
                        <EstimateGrid
                            estimate={{
                                year1CapexLabel: original.year_1_capex_label || estimate.year1CapexLabel,
                                year1OpexLabel: original.year_1_opex_label || estimate.year1OpexLabel,
                                year2CapexLabel: original.year_2_capex_label || estimate.year2CapexLabel,
                                year2OpexLabel: original.year_2_opex_label || estimate.year2OpexLabel,
                            }}
                        />
                        <p className={styles.estimateNote}>
                            Basis: {original.source_label || estimate.sourceLabel || 'Lens benchmark'} ·
                            Confidence:{' '}
                            {titleCase(original.estimate_confidence || estimate.confidence || 'Medium')}
                        </p>
                    </div>
                    <p className={styles.yearLabel}>Year 1</p>
                    <div className={styles.inputGrid}>{yearFields('year_1', form.year_1, setYear)}</div>
                    <p className={styles.yearLabel}>Year 2</p>
                    <div className={styles.inputGrid}>{yearFields('year_2', form.year_2, setYear)}</div>
                    <div className={styles.metaGrid}>
                        <label>
                            <span className={styles.fieldLabel}>Currency</span>
                            <select
                                className={styles.fieldSelect}
                                value={form.currency}
                                onChange={e => setForm(prev => ({ ...prev, currency: e.target.value }))}>
                                <option value="GBP">GBP (£)</option>
                                <option value="EUR">EUR (€)</option>
                                <option value="USD">USD ($)</option>
                            </select>
                        </label>
                        <label>
                            <span className={styles.fieldLabel}>Estimate basis</span>
                            <select
                                className={styles.fieldSelect}
                                value={form.estimate_basis}
                                onChange={e =>
                                    setForm(prev => ({ ...prev, estimate_basis: e.target.value }))
                                }>
                                <option value="human_refined_benchmark">Human refined benchmark</option>
                                <option value="management_budget">Management budget</option>
                                <option value="supplier_quote">Supplier quote</option>
                                <option value="existing_spend">Existing spend</option>
                                <option value="human_estimate">Human estimate</option>
                            </select>
                        </label>
                        <label>
                            <span className={styles.fieldLabel}>Confidence</span>
                            <select
                                className={styles.fieldSelect}
                                value={form.confidence}
                                onChange={e => setForm(prev => ({ ...prev, confidence: e.target.value }))}>
                                <option value="high">High</option>
                                <option value="medium">Medium</option>
                                <option value="low">Low</option>
                            </select>
                        </label>
                    </div>
                    <label>
                        <span className={styles.fieldLabel}>Reviewer note (optional)</span>
                        <textarea
                            className={styles.fieldArea}
                            placeholder="Reason for review, source of revised estimate…"
                            value={form.review_note}
                            onChange={e => setForm(prev => ({ ...prev, review_note: e.target.value }))}
                        />
                    </label>
                    {error ? <p className={styles.estimateNote}>{error}</p> : null}
                    <div className={styles.modalActions}>
                        <button type="button" className={styles.actionBtn} onClick={onClose}>
                            Cancel
                        </button>
                        <button
                            type="button"
                            className={styles.saveBtn}
                            disabled={saving}
                            onClick={() => onSave(form)}>
                            {saving ? 'Saving…' : 'Save reviewed estimate'}
                        </button>
                    </div>
                </div>
            </div>
        </div>,
        document.body,
    );
}

const IntelligenceInsightsRemediationInvestment = ({
    projectId,
    data,
    loading,
    error,
    onAskAi,
    onExport,
    exportEnabled = false,
    onOpenModule,
    onGenerateAnalysis,
    onGoToOverview,
    onNewEvidenceAction,
    onRetry,
    generating = false,
    historicalVersion = null,
    onExitHistoricalView = null,
}) => {
    const dispatch = useDispatch();
    const notesState = useSelector(state => state?.dashboard?.notes);
    const readOnly = useInsightsReadOnly();
    const [navKey, setNavKey] = useState('all');
    const [priorityFilter, setPriorityFilter] = useState('all');
    const [scopeFilter, setScopeFilter] = useState('all');
    const [estimateFilter, setEstimateFilter] = useState('all');
    const [reviewFilter, setReviewFilter] = useState('all');
    const [detailItem, setDetailItem] = useState(null);
    const [reviewItem, setReviewItem] = useState(null);
    const [reviewSaving, setReviewSaving] = useState(false);
    const [reviewError, setReviewError] = useState('');
    const [evidenceTarget, setEvidenceTarget] = useState(null);
    const [sidebarContent, setSidebarContent] = useState('');
    const [showTaskSidebar, setShowTaskSidebar] = useState(false);
    const [showRaidSidebar, setShowRaidSidebar] = useState(false);

    const treatments = data?.treatments || [];
    const summary = data?.summary || {};
    const nav = data?.navigation || [];

    const scopeOptions = useMemo(() => {
        const keys = new Set();
        treatments.forEach(item => (item.scopeAreaIds || []).forEach(key => keys.add(key)));
        return ['all', ...keys];
    }, [treatments]);

    const filtered = useMemo(() => {
        const allowed = NAV_HORIZONS[navKey];
        return treatments.filter(item => {
            if (allowed && !allowed.has(item.timeHorizon)) return false;
            if (priorityFilter !== 'all' && item.priority !== priorityFilter) return false;
            if (scopeFilter !== 'all' && !(item.scopeAreaIds || []).includes(scopeFilter)) return false;
            if (estimateFilter === 'estimated' && item.estimate?.status !== 'estimated') return false;
            if (estimateFilter === 'not_estimated' && item.estimate?.status === 'estimated') return false;
            if (reviewFilter === 'reviewed' && !item.estimate?.humanReviewed) return false;
            if (reviewFilter === 'lens' && item.estimate?.humanReviewed) return false;
            return true;
        });
    }, [treatments, navKey, priorityFilter, scopeFilter, estimateFilter, reviewFilter]);

    const itemPrefill = item =>
        [item?.title, item?.summary, item?.whyRecommended, item?.objective].filter(Boolean).join('\n\n');

    const handleAction = async (action, item = detailItem) => {
        if (!action || isInsightsReadOnlyBlocked(readOnly, action)) return;
        const key = action.key;
        if (key === 'view_details' || key === 'open_treatment') {
            setDetailItem(item);
            return;
        }
        if (key === 'review_estimate') {
            setDetailItem(null);
            setReviewItem(item);
            setReviewError('');
            return;
        }
        if (key === 'view_evidence' || key === 'open_evidence_panel') {
            setEvidenceTarget({
                mode: 'object',
                objectType: 'treatment',
                objectId: item?.treatmentId || item?.id,
                area: item?.scopeAreaIds?.[0] || null,
            });
            return;
        }
        if (key === 'create_task') {
            setSidebarContent(itemPrefill(item));
            setShowTaskSidebar(true);
            return;
        }
        if (key === 'add_to_raid' || key === 'add_raid') {
            setSidebarContent(itemPrefill(item));
            setShowRaidSidebar(true);
            return;
        }
        if (key === 'save_to_journal') {
            try {
                await saveInsightsModuleToJournal({
                    dispatch,
                    notesState,
                    moduleLabel: 'Remediation & Investment',
                    headline: item?.title || '',
                    bodyText: buildInsightsItemJournalBody(item) || itemPrefill(item),
                });
                toast.success('Saved to Journal');
            } catch {
                toast.error('Could not save to Journal');
            }
            return;
        }
        if (key === 'open_key_findings') {
            onOpenModule?.('key_findings', { scopeArea: action.target?.scope_area || null });
            return;
        }
        if (key === 'open_risks_signals') {
            onOpenModule?.('risks_signals');
        }
    };

    const handleSaveReview = async form => {
        if (!reviewItem || !projectId) return;
        setReviewSaving(true);
        setReviewError('');
        try {
            const url = withInsightsContextQuery(
                INTELLIGENCE_INSIGHTS_V2_REMEDIATION_ESTIMATE_REVIEW(
                    projectId,
                    reviewItem.treatmentId || reviewItem.id,
                ),
                {},
                { analysisId: data?.analysisId },
            );
            await axiosInstance.post(url, form);
            toast.success('Reviewed estimate saved');
            setReviewItem(null);
            onRetry?.();
        } catch (err) {
            const detail = err?.response?.data?.detail;
            setReviewError(typeof detail === 'string' ? detail : 'Could not save reviewed estimate.');
        } finally {
            setReviewSaving(false);
        }
    };

    const handleAskLens = useCallback(() => {
        onAskAi?.({ moduleTitle: 'Remediation & Investment' });
    }, [onAskAi]);

    if (error || !data || data.isEmpty) {
        return (
            <InsightsModuleChrome
                title={data?.title || 'Remediation & Investment'}
                subtitle={
                    data?.subtitle ||
                    'Recommended remediation, timing and indicative investment arising from the current analysis.'
                }
                metaLine={data?.metaLine || 'Entire Project'}
                onAskAi={handleAskLens}
                onExport={onExport}
                exportEnabled={exportEnabled}
                readOnly={readOnly}
                historicalVersion={historicalVersion}
                onExitHistoricalView={onExitHistoricalView}
                newEvidenceBanner={data?.newEvidenceBanner}
                onNewEvidenceAction={onNewEvidenceAction}>
                <ModuleEmptyState
                    moduleKey="remediation_investment"
                    loading={Boolean(loading && !error)}
                    error={error}
                    emptyState={data?.emptyState}
                    status={data?.status}
                    generating={generating}
                    onGoToOverview={onGoToOverview}
                    onGenerateAnalysis={onGenerateAnalysis}
                    onRetry={onRetry}
                />
            </InsightsModuleChrome>
        );
    }

    return (
        <>
            <InsightsModuleChrome
                title={data.title || 'Remediation & Investment'}
                subtitle={data.subtitle}
                metaLine={data.metaLine}
                onAskAi={handleAskLens}
                onExport={onExport}
                exportEnabled={exportEnabled}
                readOnly={readOnly}
                historicalVersion={historicalVersion}
                onExitHistoricalView={onExitHistoricalView}
                newEvidenceBanner={data.newEvidenceBanner}
                onNewEvidenceAction={onNewEvidenceAction}>
                <div className={styles.pills} role="tablist" aria-label="Timing">
                    {nav.map(item => (
                        <button
                            key={item.key}
                            type="button"
                            role="tab"
                            aria-selected={navKey === item.key}
                            className={`${styles.pill} ${navKey === item.key ? styles.pillActive : ''}`}
                            onClick={() => setNavKey(item.key)}>
                            {item.label}
                        </button>
                    ))}
                </div>

                <div className={styles.totalsBar}>
                    <div className={styles.totalsGrid}>
                        <div>
                            <p className={styles.totalLabel}>Y1 CAPEX</p>
                            <p className={styles.totalValue}>{summary.year1CapexLabel || '—'}</p>
                        </div>
                        <div>
                            <p className={styles.totalLabel}>Y1 OPEX</p>
                            <p className={styles.totalValue}>{summary.year1OpexLabel || '—'}</p>
                        </div>
                        <div>
                            <p className={styles.totalLabel}>Y2 CAPEX</p>
                            <p className={styles.totalValue}>{summary.year2CapexLabel || '—'}</p>
                        </div>
                        <div>
                            <p className={styles.totalLabel}>Y2 OPEX</p>
                            <p className={styles.totalValue}>{summary.year2OpexLabel || '—'}</p>
                        </div>
                    </div>
                    <div className={styles.totalsMeta}>
                        <p className={styles.totalsMetaTitle}>{summary.label || 'Indicative total'}</p>
                        <p className={styles.totalsMetaNote}>{summary.disclaimer}</p>
                    </div>
                </div>

                <div className={styles.filters}>
                    {[
                        ['Priority', priorityFilter, setPriorityFilter, ['all', 'critical', 'high', 'medium', 'low']],
                        ['Scope', scopeFilter, setScopeFilter, scopeOptions],
                        ['Estimate', estimateFilter, setEstimateFilter, ['all', 'estimated', 'not_estimated']],
                        ['Review status', reviewFilter, setReviewFilter, ['all', 'reviewed', 'lens']],
                    ].map(([label, value, setter, options]) => (
                        <label key={label} className={styles.filter}>
                            <span className={styles.filterLabel}>{label}</span>
                            <select
                                className={styles.filterSelect}
                                value={value}
                                onChange={e => setter(e.target.value)}
                                aria-label={label}>
                                {options.map(opt => (
                                    <option key={opt} value={opt}>
                                        {opt === 'all' ? 'All' : titleCase(opt)}
                                    </option>
                                ))}
                            </select>
                        </label>
                    ))}
                </div>

                <div className={styles.tableWrap}>
                    <table className={styles.table}>
                        <thead>
                            <tr>
                                <th>Remediation</th>
                                <th>Priority</th>
                                <th>Timing</th>
                                <th>Y1 CAPEX</th>
                                <th>Y1 OPEX</th>
                                <th>Y2 CAPEX</th>
                                <th>Y2 OPEX</th>
                                <th>Confidence</th>
                                <th>Estimate basis</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.length ? (
                                filtered.map(item => (
                                    <tr key={item.id}>
                                        <td>
                                            <button
                                                type="button"
                                                className={styles.rowBtn}
                                                onClick={() => setDetailItem(item)}>
                                                <p className={styles.rowTitle}>{item.title}</p>
                                                <p className={styles.rowSummary}>{item.summary}</p>
                                                {item.estimate?.humanReviewed ? (
                                                    <span className={styles.reviewedChip}>Human reviewed</span>
                                                ) : null}
                                            </button>
                                        </td>
                                        <td>
                                            <PriorityBadge value={item.priority} label={item.priorityLabel} />
                                        </td>
                                        <td>
                                            <span className={styles.timingChip}>{item.timeHorizonLabel}</span>
                                        </td>
                                        <td>{moneyOrDash(item.estimate?.year1CapexLabel)}</td>
                                        <td>{moneyOrDash(item.estimate?.year1OpexLabel)}</td>
                                        <td>{moneyOrDash(item.estimate?.year2CapexLabel)}</td>
                                        <td>{moneyOrDash(item.estimate?.year2OpexLabel)}</td>
                                        <td className={styles.basis}>{item.estimate?.confidence || '—'}</td>
                                        <td className={styles.basis}>{item.estimate?.sourceLabel || '—'}</td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan={9}>
                                        <p className={styles.emptyNote}>
                                            No remediations match the current filters.
                                        </p>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </InsightsModuleChrome>

            <DetailDrawer
                open={Boolean(detailItem)}
                item={detailItem}
                readOnly={readOnly}
                onClose={() => setDetailItem(null)}
                onAction={action => handleAction(action, detailItem)}
            />
            <ReviewEstimateModal
                open={Boolean(reviewItem)}
                item={reviewItem}
                saving={reviewSaving}
                error={reviewError}
                onClose={() => setReviewItem(null)}
                onSave={handleSaveReview}
            />
            <HighlightsEvidencePanel
                open={Boolean(evidenceTarget)}
                projectId={projectId}
                area={evidenceTarget?.area}
                objectType={evidenceTarget?.objectType || null}
                objectId={evidenceTarget?.objectId || null}
                endpointMode={evidenceTarget?.mode === 'object' ? 'object' : 'scope'}
                onClose={() => setEvidenceTarget(null)}
                onOpenModule={onOpenModule}
            />
            <TaskFormSidebar
                show={showTaskSidebar}
                setShow={show => {
                    setShowTaskSidebar(show);
                    if (!show) setSidebarContent('');
                }}
                defaultTask={sidebarContent || ''}
            />
            <AskAiQuickCreateSidebar
                show={showRaidSidebar}
                setShow={show => {
                    setShowRaidSidebar(show);
                    if (!show) setSidebarContent('');
                }}
                kind="raid"
                defaultText={() => sidebarContent || ''}
            />
        </>
    );
};

export default IntelligenceInsightsRemediationInvestment;
